import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import AuthorizationTokenStorage from '../auth/AuthorizationTokenStorage';
import { ERROR_CODES, EventHelperTypes } from '../exports/Constants';
import { store } from '../../store';
import HelperMethods from '../exports/HelperMethods';
import ApiMockUtility from '../utilities/ApiMockUtility';
import { RenderableErrorResponse, ReservedHttpResponseCodes, Response } from '../exports/Types';
import { ErrorHandlerInterface } from '../exports/Interfaces';
import { collect } from 'collect.js';
import GlobalAppUtility from '../utilities/GlobalAppUtility';
import i18n from 'i18next';
import pageDiagnostics from '../helpers/PageDiagnostics';

export const PREFERRED_LOCALE_HEADER = 'x-preferred-locale';

export const E2E_RATE_LIMIT_HEADER = 'x-e2e-rate-limit-key';

class BackendApiClient {
  private client: AxiosInstance;
  private authToken = null;

  private translationPath = 'Services:Api:BackendApiClient';
  private fallbackLocale = 'en';

  constructor() {
    this.client = axios.create({
      baseURL: this.getApiBaseUrl(),
      headers: {
        Authorization: `Bearer ${this.authToken}`,
        [PREFERRED_LOCALE_HEADER]: i18n.language ?? this.fallbackLocale,
        ...BackendApiClient.e2eRateLimitHeader(),
      },
    });
  }

  /**
   * axios resolves a request with status 0 (`settle` short-circuits on `!response.status`). Firefox
   * produces exactly that when a navigation cuts an XHR off: a bare `loadend` with an empty body and
   * no `error` event. Rethrowing as the network error axios would otherwise have raised keeps every
   * caller on its failure branch instead of reading `''.data`.
   */
  private static assertResponded<T>(response: AxiosResponse<T>): AxiosResponse<T> {
    if (response.status) {
      return response;
    }

    pageDiagnostics.responseLessRequest(response.config?.method, response.config?.url);

    throw new AxiosError('Request ended without a response', AxiosError.ERR_NETWORK, response.config, response.request);
  }

  /**
   * Raises the backend's per-minute rate limit for the e2e suite, and ONLY for it.
   *
   * The `api` limiter keys its bucket by user id, or by IP when unauthenticated. The suite runs as a
   * guest, so every app boot shares one IP bucket — roughly 190 calls per full run against a
   * 120/minute ceiling, which throttles by construction and gets worse as the suite grows. The
   * backend raises the ceiling to 600/minute for requests carrying this header with the matching
   * secret; everything else keeps 120.
   *
   * Set ONLY in playwright.config.ts -> webServer.env, never in .env.development — that file is
   * committed and feeds the app-dev deployment, which would put the secret in a real bundle. Vite
   * statically replaces `import.meta.env.*`, so with the variable unset this compiles to
   * `'' || undefined` and the header is never added: a production build cannot carry it even by
   * accident. The backend additionally refuses to honour it in production and fails closed when its
   * own secret is unset.
   */
  private static e2eRateLimitHeader(): Record<string, string> {
    const key = import.meta.env.VITE_E2E_RATE_LIMIT_KEY;

    return key ? { [E2E_RATE_LIMIT_HEADER]: key } : {};
  }

  public async request(
    configuration: AxiosRequestConfig,
    test?: boolean,
    errorHandlers?: ErrorHandlerInterface,
  ): Promise<any> {
    if (await this.getAuthToken()) {
      if (test) {
        return new ApiMockUtility(configuration).requestAuthorized();
      }

      return this.requestAuthorized(configuration, errorHandlers);
    }

    if (test) {
      return new ApiMockUtility(configuration).request();
    }

    return this.requestAsync(configuration, errorHandlers);
  }

  async requestAsync(configuration: AxiosRequestConfig, errorHandlers?: ErrorHandlerInterface): Promise<any> {
    try {
      const newConfig = await this.createRequestConfig({
        ...configuration,
        headers: {
          [PREFERRED_LOCALE_HEADER]: i18n.language ?? this.fallbackLocale,
          ...BackendApiClient.e2eRateLimitHeader(),
          ...configuration.headers,
        },
      });

      const response = BackendApiClient.assertResponded(await this.client.request(newConfig));

      return {
        success: true,
        response,
        status: response.status,
      };
    } catch (error) {
      this.errorHandler(error, errorHandlers);

      return {
        success: false,
        response: error,
        status: error?.response?.status,
      };
    }
  }

  async requestAuthorized(
    configuration: AxiosRequestConfig,
    errorHandlers?: ErrorHandlerInterface,
  ): Promise<Response<any>> {
    const newConfig = await this.createRequestConfig({
      ...configuration,
      headers: {
        [PREFERRED_LOCALE_HEADER]: i18n.language ?? this.fallbackLocale,
        ...BackendApiClient.e2eRateLimitHeader(),
        ...configuration.headers,
      },
    });

    if (!newConfig?.headers?.Authorization.split('Bearer ')[1]) {
      return {
        success: false,
        response: null,
        status: 401,
      };
    }

    try {
      const response = BackendApiClient.assertResponded(await this.client.request(newConfig));

      return {
        success: true,
        response,
        status: response.status,
      };
    } catch (error) {
      this.errorHandler(error, errorHandlers);

      return {
        success: false,
        response: error,
        status: error?.response?.status,
      };
    }
  }

  private errorHandler(error: RenderableErrorResponse, errorHandlers?: ErrorHandlerInterface) {
    if (!error.response?.status) {
      return;
    }

    const handler =
      {
        0: () => this.dispatchNoInternet(),
        401: () => this.handleInvalidToken(),
        [ReservedHttpResponseCodes.ErrorResponseModal]: () => {
          const data = error?.response?.data;
          const handler = collect(errorHandlers).keys().contains(data?.error_code)
            ? errorHandlers[data?.error_code]
            : null;

          // "No couriers" is rendered inline by the caller (not via the global modal) so it can
          // show the master promo code as a click-to-copy element. Hand the error to the caller.
          if (data?.error_code === ERROR_CODES.NO_COURIERS_AVAILABLE) {
            return handler?.(error);
          }

          return GlobalAppUtility.globalAppMethodsContext?.toggleInfoModal({
            openModal: true,
            title: data?.title,
            description: data?.description,
            primaryButtonTitle: i18n.t(`${this.translationPath}.info_modal.button_title`),
            onConfirm: handler,
          });
        },
      }[error.response.status] ?? (() => null);

    return handler();
  }

  async createRequestConfig(configuration: AxiosRequestConfig): Promise<AxiosRequestConfig> {
    const token = await this.getAuthToken();

    return {
      ...configuration,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
        [PREFERRED_LOCALE_HEADER]: i18n.language ?? this.fallbackLocale,
        ...configuration?.headers,
      },
    };
  }

  public async handleInvalidToken() {
    if (store.getState().eventHelper.event.type || !(await this.getAuthToken())) {
      return;
    }

    HelperMethods.triggerEventSideEffects({
      type: EventHelperTypes.LOG_OUT,
      payload: null,
    });
  }

  private getApiBaseUrl(): string | undefined {
    return import.meta.env.VITE_API_BASE_URL;
  }

  async getAuthToken(): Promise<string> {
    const { authToken } = this;

    if (authToken) {
      return authToken;
    }

    return AuthorizationTokenStorage.getToken() ?? '';
  }

  public deleteAuthToken(): void {
    this.authToken = null;
  }

  public setFallbackLocale(value: string): void {
    this.fallbackLocale = value;
  }

  public dispatchNoInternet(): void {
    HelperMethods.triggerEventSideEffects({
      type: EventHelperTypes.NO_INTERNET,
      payload: null,
    });
  }
}

export default new BackendApiClient();
